import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import {
  showMatchInbox,
  updateUserMatchStatus,
  updateUserMatchesAction
} from '../../../app/redux/actions/userMatchActions';
import { AppDispatch } from '../../../app/redux/configureStore';
import { UserMatch, UserMatchAction, UserMatchCollectStatus } from '../../../app/redux/reducers/userMatchesReducer';
import MatchesStates from './MatchesStates';

import Loader from '@/components/ui/Loader';
import { User } from '../../../app/user/profile/profileUtils';
import MatchBody from './MatchBody';
import MatchFooter from './MatchFooter';
import MatchHeader from './MatchHeader';
import MatchesInbox from './MatchesInbox';
import UserMatchPanel from './UserMatchPanel';
import { useUserMatches } from './hooks/UseUserMatches';

interface UserMatchSlideOverProps extends React.HTMLAttributes<HTMLDivElement> {
  open: boolean;
  user: User;
  setOpen: (status: boolean) => void;
  updateStatus: AppDispatch;
  updateMatches: AppDispatch;
  showMatchInbox: AppDispatch;
}

const UserMatchSlideOver = ({
  user,
  open,
  setOpen,
  updateStatus,
  updateMatches,
  showMatchInbox
}: UserMatchSlideOverProps) => {
  const isMounted = useRef(true);
  const timersRef = useRef([]);

  const {
    status,
    numMatches,
    nextItem,
    previousItem,
    currentItem,
    selectedMatches,
    showInbox,
    transitionHandlerAll,
    transitionHandler,
    updateCurrentItemById,
    resetInbox,
    currentItemIndex
  } = useUserMatches();

  const hasMatches = numMatches > 0;
  const localStorageInitialCount = `MatchesShowIntro_${user?.id}`;

  useEffect(() => {
    return () => {
      isMounted.current = false;
      // Clear all timers in the ref
      timersRef.current.forEach((timerId) => {
        clearTimeout(timerId);
      });
    };
  }, []);

  const handleTransition = (action: UserMatchAction, match?: UserMatch) => {
    if (isMounted.current) {
      const timing = action === UserMatchAction.Seen ? 0 : 500;
      const matchToTransition = match ?? currentItem;
      updateStatus(UserMatchCollectStatus.Pending);
      const timer = setTimeout(() => {
        transitionHandler(updateMatches, action, matchToTransition);
        updateStatus(UserMatchCollectStatus.Updated);
      }, timing);

      timersRef.current.push(timer);
    }
  };

  const handleAllTransition = (action: UserMatchAction, status: boolean) => {
    if (selectedMatches.length === 0) return;
    updateStatus(UserMatchCollectStatus.Pending);
    const timer = setTimeout(() => {
      if (isMounted.current) {
        transitionHandlerAll(updateMatches, action, status);
        updateStatus(UserMatchCollectStatus.Updated);
      }
      timersRef.current.push(timer);
    }, 400);
  };

  const handleClosePanel = () => {
    setOpen(false);
    resetInbox();
    showMatchInbox(true);
  };

  const dialogTitle = (
    <MatchHeader match={currentItem} key={currentItem ? 'userMatchId' + currentItem.userMatchId : null} />
  );

  const matchFooter = (
    <MatchFooter
      currentItemIndex={currentItemIndex}
      previousItem={previousItem}
      nextItem={nextItem}
      transitionHandler={handleTransition}
      transitionAllHandler={handleAllTransition}
    />
  );

  let dialogBody;
  const introCount =
    typeof localStorage !== 'undefined' ? parseInt(localStorage.getItem(localStorageInitialCount) ?? '0') : 0;
  let [showIntro, setShowIntro] = useState(introCount < 2);

  if (hasMatches) {
    if (showInbox) {
      dialogBody = <MatchesInbox updateCurrentItemById={updateCurrentItemById} handleTransition={handleTransition} />;
    } else {
      if (currentItem) {
        dialogBody = <MatchBody match={currentItem} handleTransition={handleTransition} />;
      }
    }
  } else {
    dialogBody = <MatchesStates complete={true} />;
  }

  if (status == UserMatchCollectStatus.Pending && !hasMatches) {
    showIntro = false;
    dialogBody = (
      <div className="z-[1] align-middle justify-center flex flex-1 flex-col h-full">
        <div className="h-48">
          <Loader loaded={false}></Loader>
        </div>
      </div>
    );
  }

  return (
    <UserMatchPanel
      showIntro={showIntro}
      dialogTitle={dialogTitle}
      dialogBody={dialogBody}
      matchFooter={matchFooter}
      open={open}
      closePanel={handleClosePanel}
      setShowIntro={setShowIntro}
    />
  );
};

const mapStateToProps = ({ auth }: any) => {
  return {
    user: auth.user
  };
};

const mapActionsToProps = {
  updateMatches: updateUserMatchesAction,
  updateStatus: updateUserMatchStatus,
  showMatchInbox: showMatchInbox
};

export default connect(mapStateToProps, mapActionsToProps)(UserMatchSlideOver);
